import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const AppStateContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within AppStateProvider');
  }
  return context;
};

export const AppStateProvider = ({ children }) => {
  const { token } = useAuth();
  const [folders, setFolders] = useState([]);
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [promptFolders, setPromptFolders] = useState([]);
  const [scratchpad, setScratchpad] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentView, setCurrentView] = useState('command-center');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Axios config with auth header
  const getConfig = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  // Load all data on mount
  useEffect(() => {
    if (token) {
      fetchAllData();
    }
  }, [token]);

  const fetchAllData = async () => {
    try {
      const [foldersRes, itemsRes, leadsRes, ideasRes, portfolioRes, promptsRes, promptFoldersRes, scratchpadRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/folders`, getConfig()),
        axios.get(`${BACKEND_URL}/api/items`, getConfig()),
        axios.get(`${BACKEND_URL}/api/leads`, getConfig()),
        axios.get(`${BACKEND_URL}/api/ideas`, getConfig()),
        axios.get(`${BACKEND_URL}/api/portfolio`, getConfig()),
        axios.get(`${BACKEND_URL}/api/prompts`, getConfig()),
        axios.get(`${BACKEND_URL}/api/prompt-folders`, getConfig()),
        axios.get(`${BACKEND_URL}/api/scratchpad`, getConfig())
      ]);

      setFolders(foldersRes.data);
      setItems(itemsRes.data);
      setLeads(leadsRes.data);
      setIdeas(ideasRes.data);
      setPortfolio(portfolioRes.data);
      setPrompts(promptsRes.data);
      setPromptFolders(promptFoldersRes.data);
      setScratchpad(scratchpadRes.data.content);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Folder operations
  const addFolder = async (name, category) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/folders`,
        { name, category },
        getConfig()
      );
      setFolders([...folders, response.data]);
      return response.data;
    } catch (error) {
      console.error('Error adding folder:', error);
      throw error;
    }
  };

  const updateFolder = async (id, updates) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/folders/${id}`,
        updates,
        getConfig()
      );
      setFolders(folders.map(f => f.id === id ? response.data : f));
      return response.data;
    } catch (error) {
      console.error('Error updating folder:', error);
      throw error;
    }
  };

  const deleteFolder = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/folders/${id}`, getConfig());
      setFolders(folders.filter(f => f.id !== id));
      setItems(items.filter(i => i.folderId !== id));
    } catch (error) {
      console.error('Error deleting folder:', error);
      throw error;
    }
  };

  // Item operations
  const addItem = async (title, url, category, folderId = null) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/items`,
        { title, url, category, folderId },
        getConfig()
      );
      setItems([...items, response.data]);
      return response.data;
    } catch (error) {
      console.error('Error adding item:', error);
      throw error;
    }
  };

  const updateItem = async (id, updates) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/items/${id}`,
        updates,
        getConfig()
      );
      setItems(items.map(i => i.id === id ? response.data : i));
      return response.data;
    } catch (error) {
      console.error('Error updating item:', error);
      throw error;
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/items/${id}`, getConfig());
      setItems(items.filter(i => i.id !== id));
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  };

  // Lead operations
  const addLead = async (businessName, niche, category, status = 'Research') => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/leads`,
        { businessName, niche, category, status },
        getConfig()
      );
      setLeads([...leads, response.data]);
      return response.data;
    } catch (error) {
      console.error('Error adding lead:', error);
      throw error;
    }
  };

  const updateLead = async (id, updates) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/leads/${id}`,
        updates,
        getConfig()
      );
      setLeads(leads.map(l => l.id === id ? response.data : l));
      return response.data;
    } catch (error) {
      console.error('Error updating lead:', error);
      throw error;
    }
  };

  const deleteLead = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/leads/${id}`, getConfig());
      setLeads(leads.filter(l => l.id !== id));
    } catch (error) {
      console.error('Error deleting lead:', error);
      throw error;
    }
  };

  // Scratchpad operations
  const updateScratchpad = async (content) => {
    setScratchpad(content);
    // Debounce the API call
    if (updateScratchpad.timeout) {
      clearTimeout(updateScratchpad.timeout);
    }
    updateScratchpad.timeout = setTimeout(async () => {
      try {
        await axios.put(
          `${BACKEND_URL}/api/scratchpad`,
          { content },
          getConfig()
        );
      } catch (error) {
        console.error('Error updating scratchpad:', error);
      }
    }, 1000);
  };

  // Idea operations
  const addIdea = async (title, content) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ideas`,
        { title, content },
        getConfig()
      );
      setIdeas([response.data, ...ideas]);
      return response.data;
    } catch (error) {
      console.error('Error adding idea:', error);
      throw error;
    }
  };

  const updateIdea = async (id, updates) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/ideas/${id}`,
        updates,
        getConfig()
      );
      setIdeas(ideas.map(i => i.id === id ? response.data : i));
      return response.data;
    } catch (error) {
      console.error('Error updating idea:', error);
      throw error;
    }
  };

  const deleteIdea = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/ideas/${id}`, getConfig());
      setIdeas(ideas.filter(i => i.id !== id));
    } catch (error) {
      console.error('Error deleting idea:', error);
      throw error;
    }
  };

  // Portfolio operations
  const addPortfolioItem = async (title, url, description, tags, category) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/portfolio`,
        { title, url, description, tags, category },
        getConfig()
      );
      setPortfolio([response.data, ...portfolio]);
      return response.data;
    } catch (error) {
      console.error('Error adding portfolio item:', error);
      throw error;
    }
  };

  const updatePortfolioItem = async (id, updates) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/portfolio/${id}`,
        updates,
        getConfig()
      );
      setPortfolio(portfolio.map(i => i.id === id ? response.data : i));
      return response.data;
    } catch (error) {
      console.error('Error updating portfolio item:', error);
      throw error;
    }
  };

  const deletePortfolioItem = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/portfolio/${id}`, getConfig());
      setPortfolio(portfolio.filter(i => i.id !== id));
    } catch (error) {
      console.error('Error deleting portfolio item:', error);
      throw error;
    }
  };

  // Prompt operations
  const addPrompt = async (name, text, folderId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/prompts`,
        { name, text, folderId },
        getConfig()
      );
      setPrompts([response.data, ...prompts]);
      return response.data;
    } catch (error) {
      console.error('Error adding prompt:', error);
      throw error;
    }
  };

  const updatePrompt = async (id, updates) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/prompts/${id}`,
        updates,
        getConfig()
      );
      setPrompts(prompts.map(p => p.id === id ? response.data : p));
      return response.data;
    } catch (error) {
      console.error('Error updating prompt:', error);
      throw error;
    }
  };

  const deletePrompt = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/prompts/${id}`, getConfig());
      setPrompts(prompts.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting prompt:', error);
      throw error;
    }
  };

  // Prompt Folder operations
  const addPromptFolder = async (name) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/prompt-folders`,
        { name },
        getConfig()
      );
      setPromptFolders([...promptFolders, response.data]);
      return response.data;
    } catch (error) {
      console.error('Error adding prompt folder:', error);
      throw error;
    }
  };

  const updatePromptFolder = async (id, updates) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/prompt-folders/${id}`,
        updates,
        getConfig()
      );
      setPromptFolders(promptFolders.map(f => f.id === id ? response.data : f));
      return response.data;
    } catch (error) {
      console.error('Error updating prompt folder:', error);
      throw error;
    }
  };

  const deletePromptFolder = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/prompt-folders/${id}`, getConfig());
      setPromptFolders(promptFolders.filter(f => f.id !== id));
      setPrompts(prompts.filter(p => p.folderId !== id));
    } catch (error) {
      console.error('Error deleting prompt folder:', error);
      throw error;
    }
  };

  // Refresh items to get updated click counts
  const refreshItems = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/items`, getConfig());
      setItems(response.data);
    } catch (error) {
      console.error('Error refreshing items:', error);
    }
  };

  const value = {
    folders,
    items,
    leads,
    ideas,
    portfolio,
    prompts,
    promptFolders,
    scratchpad,
    setScratchpad: updateScratchpad,
    selectedItem,
    setSelectedItem,
    currentView,
    setCurrentView,
    searchQuery,
    setSearchQuery,
    loading,
    addFolder,
    updateFolder,
    deleteFolder,
    addItem,
    updateItem,
    deleteItem,
    refreshItems,
    addLead,
    updateLead,
    deleteLead,
    addIdea,
    updateIdea,
    deleteIdea,
    addPortfolioItem,
    updatePortfolioItem,
    deletePortfolioItem,
    addPrompt,
    updatePrompt,
    deletePrompt,
    addPromptFolder,
    updatePromptFolder,
    deletePromptFolder
  };

  return (
    <AppStateContext.Provider value={value}>
      {children}
    </AppStateContext.Provider>
  );
};
